import React, { useEffect, useState } from "react";
import { UserProvider, useUser } from "./contexts/UserContext";
import { SalesMembersProvider } from "./contexts/SalesMembersContext";
import { ToastProvider } from "./components/Toast";
import Login from "./components/Login";
import { apiFetch } from "./utils/api";
import { LoadingState } from "./components/ui";
import Dashboard from "./components/Dashboard";
import EngineerList from "./components/EngineerList";
import SkillSheetManager from "./components/SkillSheetManager";
import InterviewManager from "./components/InterviewManager";
import HREvaluation from "./components/HREvaluation";
import RecruitmentMarketing from "./components/RecruitmentMarketing";
import Settings from "./components/Settings";
import CsCaseManager from "./components/CsCaseManager";
import Calendar from "./components/Calendar";
import DealPipeline from "./components/DealPipeline";
import UtilizationDashboard from "./components/UtilizationDashboard";
import ActivityTimeline from "./components/ActivityTimeline";
import UserManagement from "./components/UserManagement";

const CS_CASE_ALLOWED = ['y-okada@1dr.co.jp'];
const PAGE_KEY = 'prodia_active_page';

const INTERVIEWS_ALLOWED = [
  'kamiya@1dr.co.jp',      // 上谷
  'asai@1dr.co.jp',        // 浅井
  'a-inagaki@1dr.co.jp',   // 稲垣
  't-nukumizu@1dr.co.jp',  // 温水
  'h-setoyama@1dr.co.jp',  // 瀬戸山
  'i-uemae@1dr.co.jp',     // 上前
  'y-okada@1dr.co.jp',     // 岡田
  'n-yamaguchi@1dr.co.jp', // 山口
  'r-noda@1dr.co.jp',      // 野田
  's-hattori@1dr.co.jp',   // 服部
  'h-imamura@1dr.co.jp',   // 今村
];

function NavSection({ label, collapsed, children }) {
  return (
    <>
      {!collapsed && label && (
        <div className="px-4 pb-1 pt-1">
          <span className="text-[10px] font-semibold text-prodia-text-muted uppercase tracking-widest">{label}</span>
        </div>
      )}
      <div className={collapsed ? 'px-2 space-y-0.5' : 'px-2 space-y-0.5'}>{children}</div>
    </>
  );
}

function NavButton({ isActive, onClick, iconClass, label, collapsed }) {
  return (
    <button
      type="button"
      title={collapsed ? label : undefined}
      onClick={onClick}
      className={`relative group w-full transition-colors duration-150 ${
        collapsed
          ? 'flex items-center justify-center p-2.5 rounded-lg'
          : 'flex items-center gap-3 px-3 py-2 rounded-lg text-left'
      } ${
        isActive
          ? 'bg-prodia-primary-subtle text-prodia-text-primary'
          : 'text-prodia-text-secondary hover:bg-prodia-background hover:text-prodia-text-primary'
      }`}
    >
      {isActive && (
        <span className="absolute left-0 inset-y-0 my-auto w-[3px] h-[55%] rounded-r-full bg-prodia-primary" />
      )}
      <div
        className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${
          isActive
            ? 'bg-prodia-primary text-prodia-primary-foreground'
            : 'text-prodia-text-muted group-hover:text-prodia-text-secondary'
        }`}
      >
        <i className={`${iconClass} text-xs`} aria-hidden="true" />
      </div>
      {!collapsed && <span className="text-[13px] font-medium truncate">{label}</span>}
    </button>
  );
}

function MainLayout({ page, setPage, handleLogout }) {
  const { user } = useUser();
  const canViewCsCases = user && CS_CASE_ALLOWED.includes(user.email);
  const canManageUsers = user?.permissions?.can_manage_sales_members === true;
  const [collapsed, setCollapsed] = useState(false);
  
  // 権限チェック：面談履歴・人事評価・採用マーケティングページにアクセス権限がない場合はダッシュボードにリダイレクト
  React.useEffect(() => {
    if (page === "interviews" && user && !INTERVIEWS_ALLOWED.includes(user.email)) {
      setPage("dashboard");
    }
    if (page === "hr-evaluation" && user && user.email !== 'kamiya@1dr.co.jp' && user.email !== 'asai@1dr.co.jp') {
      setPage("dashboard");
    }
    if (page === "recruitment-marketing") {
      setPage("dashboard");
    }
    if (page === "cs-cases" && !canViewCsCases) {
      setPage("dashboard");
    }
    if (page === "user-management" && !canManageUsers) {
      setPage("dashboard");
    }
  }, [page, user, setPage, canViewCsCases, canManageUsers]);
  
  return (
    <div className="flex h-screen bg-prodia-background">
      <aside
        className={`${
          collapsed ? 'w-[60px]' : 'w-56'
        } bg-prodia-surface border-r border-prodia-border flex flex-col flex-shrink-0 transition-all duration-200 overflow-hidden shadow-prodia`}
      >
        <div className={`flex items-center h-14 border-b border-prodia-border flex-shrink-0 ${
          collapsed ? 'justify-center px-2' : 'justify-between px-4'
        }`}>
          {!collapsed && (
            <div>
              <h1 className="text-base font-semibold text-prodia-text-primary tracking-tight leading-none">Prodia</h1>
              <p className="text-prodia-text-muted text-[10px] mt-0.5">Management System</p>
            </div>
          )}
          {collapsed && (
            <span className="text-base font-bold text-prodia-primary">P</span>
          )}
          <button
            type="button"
            onClick={() => setCollapsed(c => !c)}
            className="w-7 h-7 rounded-lg flex items-center justify-center text-prodia-text-muted hover:bg-prodia-background hover:text-prodia-text-primary transition-colors flex-shrink-0"
            aria-label={collapsed ? 'サイドバーを展開' : 'サイドバーを折りたたむ'}
          >
            <i className={`fas ${
              collapsed ? 'fa-chevron-right' : 'fa-chevron-left'
            } text-[10px]`}></i>
          </button>
        </div>

        {!collapsed && user?.name && (
          <div className="px-4 py-2 text-xs font-medium text-prodia-text-secondary border-b border-prodia-border truncate">
            {user.name} さん
          </div>
        )}

        <nav className="flex-1 overflow-y-auto py-2">
          <NavSection label="概要" collapsed={collapsed}>
            <NavButton
              isActive={page === "dashboard"}
              onClick={() => setPage("dashboard")}
              iconClass="fas fa-tachometer-alt"
              label="ダッシュボード"
              collapsed={collapsed}
            />
          </NavSection>

          <div className="mx-3 my-2 h-px bg-prodia-border" />

          <NavSection label="営業・人材" collapsed={collapsed}>
            <NavButton
              isActive={page === "utilization"}
              onClick={() => setPage("utilization")}
              iconClass="fas fa-user-check"
              label="稼働率管理"
              collapsed={collapsed}
            />
            <NavButton
              isActive={page === "engineers"}
              onClick={() => setPage("engineers")}
              iconClass="fas fa-users"
              label="エンジニア管理"
              collapsed={collapsed}
            />
            {user && INTERVIEWS_ALLOWED.includes(user.email) && (
              <NavButton
                isActive={page === "skill-sheets"}
                onClick={() => setPage("skill-sheets")}
                iconClass="fas fa-file-alt"
                label="スキルシート管理"
                collapsed={collapsed}
              />
            )}
            {user && INTERVIEWS_ALLOWED.includes(user.email) && (
              <NavButton
                isActive={page === "interviews"}
                onClick={() => setPage("interviews")}
                iconClass="fas fa-handshake"
                label="お客様面談履歴"
                collapsed={collapsed}
              />
            )}
            <NavButton
              isActive={page === "pipeline"}
              onClick={() => setPage("pipeline")}
              iconClass="fas fa-th-list"
              label="案件回収管理"
              collapsed={collapsed}
            />
          </NavSection>

          {((user && (user.email === 'kamiya@1dr.co.jp' || user.email === 'asai@1dr.co.jp')) || canViewCsCases) && (
            <>
              <div className="mx-3 my-2 h-px bg-prodia-border" />
              <NavSection label="組織" collapsed={collapsed}>
                {user && (user.email === 'kamiya@1dr.co.jp' || user.email === 'asai@1dr.co.jp') && (
                  <NavButton
                    isActive={page === "hr-evaluation"}
                    onClick={() => setPage("hr-evaluation")}
                    iconClass="fas fa-star"
                    label="人事評価"
                    collapsed={collapsed}
                  />
                )}
                {canViewCsCases && (
                  <NavButton
                    isActive={page === "cs-cases"}
                    onClick={() => setPage("cs-cases")}
                    iconClass="fas fa-clipboard-list"
                    label="CS案件管理"
                    collapsed={collapsed}
                  />
                )}
              </NavSection>
            </>
          )}

          <div className="mx-3 my-2 h-px bg-prodia-border" />

          <NavSection label="ツール" collapsed={collapsed}>
            <NavButton
              isActive={page === "timeline"}
              onClick={() => setPage("timeline")}
              iconClass="fas fa-history"
              label="タイムライン"
              collapsed={collapsed}
            />
            <NavButton
              isActive={page === "calendar"}
              onClick={() => setPage("calendar")}
              iconClass="fas fa-calendar-alt"
              label="カレンダー"
              collapsed={collapsed}
            />
            {canManageUsers && (
              <NavButton
                isActive={page === "user-management"}
                onClick={() => setPage("user-management")}
                iconClass="fas fa-users-cog"
                label="ユーザー管理"
                collapsed={collapsed}
              />
            )}
            <NavButton
              isActive={page === "settings"}
              onClick={() => setPage("settings")}
              iconClass="fas fa-cog"
              label="設定"
              collapsed={collapsed}
            />
          </NavSection>
        </nav>
        
        <div className={`border-t border-prodia-border flex-shrink-0 ${collapsed ? 'p-2' : 'px-3 py-3'}`}>
          <button
            type="button"
            title={collapsed ? 'ログアウト' : undefined}
            className={`w-full flex items-center rounded-lg text-prodia-text-secondary hover:text-danger hover:bg-danger-bg transition-colors ${
              collapsed ? 'justify-center p-2.5' : 'gap-2.5 px-3 py-2'
            }`}
            onClick={() => handleLogout()}
          >
            <i className="fas fa-sign-out-alt text-xs"></i>
            {!collapsed && <span className="text-[13px] font-medium">ログアウト</span>}
          </button>
          {!collapsed && (
            <div className="mt-1 px-3">
              <p className="text-[10px] text-prodia-text-muted">© 2025 Prodia Corp.</p>
            </div>
          )}
        </div>
      </aside>

      <main className="flex-1 overflow-auto min-w-0">
        {page === "dashboard" && <Dashboard onNavigate={setPage} />}
        {page === "utilization" && <UtilizationDashboard />}
        {page === "timeline" && <ActivityTimeline />}
        {page === "engineers" && <EngineerList />}
        {page === "skill-sheets" && <SkillSheetManager />}
        {page === "interviews" && <InterviewManager />}
        {page === "hr-evaluation" && <HREvaluation />}
        {page === "recruitment-marketing" && <RecruitmentMarketing />}
        {page === "cs-cases" && <CsCaseManager />}
        {page === "pipeline" && <DealPipeline />}
        {page === "calendar" && <Calendar />}
        {page === "user-management" && canManageUsers && <UserManagement />}
        {page === "settings" && <Settings />}
      </main>
    </div>
  );
}

// セッションタイムアウト設定（JWTアクセストークン 8hと整合）

function AppContent() {
  const { user, setUser, authInitializing } = useUser();
  const [page, setPage] = useState(() => localStorage.getItem(PAGE_KEY) || "dashboard");
  const [showLogoutMsg, setShowLogoutMsg] = useState(null);

  useEffect(() => {
    localStorage.setItem(PAGE_KEY, page);
  }, [page]);

  const handleLogin = () => {
    setShowLogoutMsg(null);
    setPage("dashboard");
  };

  const handleLogout = async (msg = 'ログアウトしました') => {
    const refresh = localStorage.getItem('prodia_refresh');
    if (refresh) {
      await apiFetch('/auth/logout/', {
        method: 'POST',
        body: JSON.stringify({ refresh }),
      }).catch(() => {});
    }
    setUser(null);
    setShowLogoutMsg(msg);
    setPage("dashboard");
    setTimeout(() => setShowLogoutMsg(null), 4000);
  };

  if (authInitializing) {
    return (
      <div className="min-h-screen bg-prodia-background flex items-center justify-center">
        <LoadingState message="認証情報を確認しています..." />
      </div>
    );
  }

  if (!user) {
    return <Login onLogin={handleLogin} logoutMsg={showLogoutMsg} />;
  }

  return (
    <SalesMembersProvider>
      <MainLayout page={page} setPage={setPage} handleLogout={handleLogout} />
    </SalesMembersProvider>
  );
}

function App() {
  return (
    <ToastProvider>
      <UserProvider>
        <AppContent />
      </UserProvider>
    </ToastProvider>
  );
}

export default App;
